// entityRegistry · single source of truth for the universal
// RelationshipPicker. Every entity that can be picked, created, edited,
// or viewed inline registers itself here. Adding a new entity type
// (Supplier, Ship, Hotel, Loyalty Program, …) requires only appending
// a new descriptor — no changes to RelationshipPicker itself.
//
// Descriptor shape:
//   listPath           GET → { <listKey>: Array<record> }
//   listKey            Property on the list response that contains the array.
//   createPath         POST → returns the created record (or wrapped).
//   detailPath(id)     GET  → single record (optional; falls back to list cache).
//   updatePath(id)     PATCH → returns updated record.
//   toCreatePayload    (rawInput: string, form?: object) → payload for POST.
//   toUpdatePayload    (form: object) → payload for PATCH.
//   toFormShape        (record) → editable form shape for the edit drawer.
//   formSchema         Ordered array of field descriptors driving the drawer:
//                        { key, label, type: 'text'|'email'|'date'|'textarea', placeholder?, span?: 1|2 }
//   labelOf            (record) → primary display label (chip / dropdown row).
//   subtitleOf         (record) → optional secondary line (dropdown rows).
//   icon               lucide-react icon component used in chips/menus.
//   entityLabel        Singular label ("Traveler") for empty-state + toasts.
//   entityLabelPlural  ("Travelers") for headers/search placeholders.
//   themeTint          Tailwind classes for resolved chips.
//   quickCreatable     boolean — whether "Create X 'foo'" appears in menu.

import { UserCheck, Building2, Ship } from "lucide-react";

const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

// ── Traveler ────────────────────────────────────────────────────────
const traveler = {
  entityLabel: "Traveler",
  entityLabelPlural: "Travelers",
  icon: UserCheck,
  themeTint: "bg-[#EAF2FD] text-[#0952a1]",
  quickCreatable: true,

  listPath: "/api/travelers",
  listKey: "travelers",
  createPath: "/api/travelers",
  updatePath: (id) => `/api/travelers/${id}`,

  labelOf: (r) => r?.name || r?.preferred_name || "Untitled",
  subtitleOf: (r) => r?.nationality || null,

  toCreatePayload: (raw) => {
    const parts = String(raw || "").trim().split(/\s+/);
    return {
      name: raw.trim(),
      first_name: parts[0] || raw.trim(),
      last_name: parts.length > 1 ? parts.slice(-1)[0] : "",
      middle_name: parts.length > 2 ? parts.slice(1, -1).join(" ") : "",
    };
  },

  toFormShape: (r) => ({
    name: r.name || "",
    preferred_name: r.preferred_name || "",
    email: r.email || "",
    date_of_birth: (r.date_of_birth || "").slice(0, 10),
    nationality: r.nationality || "",
    passport_number_masked: r.passport?.number_masked || "",
    passport_expires: (r.passport?.expires || "").slice(0, 10),
  }),

  toUpdatePayload: (f) => ({
    name: (f.name || "").trim(),
    preferred_name: (f.preferred_name || "").trim() || null,
    email: (f.email || "").trim() || null,
    date_of_birth: f.date_of_birth || null,
    nationality: f.nationality || null,
    passport_number_masked: f.passport_number_masked || null,
    passport_expires: f.passport_expires || null,
  }),

  formSchema: [
    { key: "name",                   label: "Legal name",       type: "text",  span: 2, required: true },
    { key: "preferred_name",         label: "Preferred name",   type: "text",  span: 2, placeholder: "How they like to be called" },
    { key: "email",                  label: "Email",            type: "email", span: 1 },
    { key: "date_of_birth",          label: "Date of birth",    type: "date",  span: 1 },
    { key: "nationality",            label: "Nationality (2-letter code)", type: "text", span: 2, placeholder: "e.g. US, GB, DE" },
    { key: "passport_number_masked", label: "Passport number",  type: "text",  span: 1 },
    { key: "passport_expires",       label: "Passport expiry",  type: "date",  span: 1 },
  ],
};

// ── Supplier (P1 stub) ──────────────────────────────────────────────
// Endpoints don't fully exist yet — the descriptor is scaffolded so the
// RelationshipPicker can render a helpful "coming soon" state when
// invoked with `entityType="supplier"` today, and light up automatically
// once the /api/suppliers CRUD is delivered.
const supplier = {
  entityLabel: "Supplier",
  entityLabelPlural: "Suppliers",
  icon: Building2,
  themeTint: "bg-[#F3E8FF] text-[#6D28D9]",
  quickCreatable: true,
  scaffold: true,   // ← picked up by RelationshipPicker to show placeholder.

  listPath: "/api/suppliers",
  listKey: "suppliers",
  createPath: "/api/suppliers",
  updatePath: (id) => `/api/suppliers/${id}`,

  labelOf: (r) => r?.name || r?.legal_name || "Untitled supplier",
  subtitleOf: (r) => r?.category || null,

  toCreatePayload: (raw) => ({ name: String(raw || "").trim() }),
  toFormShape: (r) => ({
    name: r.name || "",
    category: r.category || "",
    website: r.website || "",
    notes: r.notes || "",
  }),
  toUpdatePayload: (f) => ({
    name: (f.name || "").trim(),
    category: (f.category || "").trim() || null,
    website: (f.website || "").trim() || null,
    notes: (f.notes || "").trim() || null,
  }),
  formSchema: [
    { key: "name",     label: "Name",     type: "text",     span: 2, required: true },
    { key: "category", label: "Category", type: "text",     span: 1, placeholder: "Travel agent, Excursion, Insurance…" },
    { key: "website",  label: "Website",  type: "text",     span: 1, placeholder: "https://…" },
    { key: "notes",    label: "Notes",    type: "textarea", span: 2 },
  ],
};

// ── Ship (P1 stub) ──────────────────────────────────────────────────
const ship = {
  entityLabel: "Ship",
  entityLabelPlural: "Ships",
  icon: Ship,
  themeTint: "bg-[#E0F2FE] text-[#075985]",
  quickCreatable: false,   // ships come from CKB — no ad-hoc create.
  scaffold: true,

  listPath: "/api/ckb/ships",
  listKey: "ships",
  createPath: null,
  updatePath: null,

  labelOf: (r) => r?.name || "Untitled ship",
  subtitleOf: (r) => r?.line_name || r?.line_id || null,

  toCreatePayload: () => ({}),
  toFormShape: (r) => ({ name: r?.name || "", line_id: r?.line_id || "" }),
  toUpdatePayload: (f) => ({ name: f.name || "" }),
  formSchema: [
    { key: "name",    label: "Ship name", type: "text", span: 2, required: true },
    { key: "line_id", label: "Cruise line ID", type: "text", span: 2 },
  ],
};

const REGISTRY = { traveler, supplier, ship };

export function getDescriptor(entityType) {
  const d = REGISTRY[entityType];
  if (!d) {
    throw new Error(
      `RelationshipPicker · unknown entityType "${entityType}". ` +
      `Known: ${Object.keys(REGISTRY).join(", ")}`,
    );
  }
  return d;
}

export function matchesQuery(descriptor, record, query) {
  const q = norm(query);
  if (!q) return true;
  const hay = [descriptor.labelOf(record), descriptor.subtitleOf(record)]
    .filter(Boolean).join(" ");
  return norm(hay).includes(q);
}

export { REGISTRY, norm };
